(function () {
  'use strict';

  const numericId = (id) => String(id || '').split('/').pop();
  const nonNegative = (value) => {
    if (value == null || String(value).trim() === '') return null;
    const number = Number(value);
    return Number.isFinite(number) && number >= 0 ? number : null;
  };
  const money = (value) => {
    const amount = nonNegative(value?.amount);
    const currency = value?.currencyCode;
    return amount != null && /^[A-Z]{3}$/.test(currency || '')
      // Storefront unit prices can contain fractions of a cent. Preserve
      // them through tax conversion; round only the final displayed amount.
      ? { cents: amount * 100, currency } : null;
  };
  const element = (tag, className, text) => {
    const node = document.createElement(tag);
    node.className = className;
    if (text != null) node.textContent = text;
    return node;
  };

  // Match the native quantity-one card policy. Never join a price from one
  // variant to the unit measurement or compare-at price of another variant.
  function priceContext(product, settings) {
    const connection = product?.graphql?.rccPriceVariants;
    const nodes = Array.isArray(connection?.nodes) ? connection.nodes : [];
    const appVariants = Array.isArray(product?.variantsAll) ? product.variantsAll : [];
    const grouped = String(product?.productId || '').includes('@');
    const selectedId = numericId(product?.variantId);
    const source = grouped ? (appVariants.length ? appVariants
      : nodes.filter((variant) => numericId(variant?.id) === selectedId)) : nodes;
    const candidates = source.map((variant) => {
      const detail = nodes.find((node) => numericId(node?.id) === numericId(variant?.id));
      return { ...variant, ...detail };
    }).filter((variant) => money(variant.price));
    const selected = candidates.find((variant) => numericId(variant.id) === selectedId)
      || appVariants.find((variant) => numericId(variant?.id) === selectedId && money(variant.price));
    const range = grouped ? product?.priceRange : product?.graphql?.rccPriceRange || product?.priceRange;
    const minimum = money(range?.minVariantPrice);
    const maximum = money(range?.maxVariantPrice);
    // Nimstrata scopes variantsAll to the card, but can retain the parent
    // product's price range. A card with one represented variant has no "Ab".
    const rangeVaries = !(grouped && appVariants.length === 1)
      && minimum && maximum && minimum.currency === maximum.currency
      && minimum.cents < maximum.cents;

    const current = product?.priceInfo;
    const currentFallback = money({ amount: current?.price, currencyCode: current?.currencyCode })
      ? { variant: { price: { amount: current.price, currencyCode: current.currencyCode } }, from: false } : null;
    if (settings.priceDisplay === 'SELECTED') return selected ? { variant: selected, from: false } : currentFallback;

    const currency = minimum?.currency || money(candidates[0]?.price)?.currency;
    const sameCurrency = candidates.filter((variant) => money(variant.price).currency === currency);
    const lowest = sameCurrency.reduce((best, variant) =>
      !best || money(variant.price).cents < money(best.price).cents ? variant : best, null);
    const complete = !grouped && connection?.pageInfo?.hasNextPage === false
      && candidates.length === nodes.length && nodes.length > 0
      && sameCurrency.length === candidates.length;
    const matchesMinimum = lowest && minimum
      && money(lowest.price).currency === minimum.currency
      && money(lowest.price).cents === minimum.cents;

    if (lowest && (complete || matchesMinimum)) {
      return {
        variant: lowest,
        from: Boolean(rangeVaries || (complete && sameCurrency.some((variant) =>
          money(variant.price).cents !== money(lowest.price).cents))),
      };
    }
    // Partial batches cannot establish a lowest variant. Retain an exact
    // selected variant when possible; otherwise the API range supplies only
    // the main price, without invented unit or sale information.
    if (selected) return { variant: selected, from: false };
    if (!grouped && minimum) return { variant: { price: range.minVariantPrice }, from: Boolean(rangeVaries) };
    return currentFallback;
  }

  class RccProductPrice extends HTMLElement {
    static get graphqlRequirements() {
      return {
        graphql: [
          `rccPriceTaxRate: metafield(namespace: "custom", key: "steuersatz") { value }
          rccPriceRange: priceRange {
            minVariantPrice { amount currencyCode }
            maxVariantPrice { amount currencyCode }
          }
          rccPriceVariants: variants(first: 100) {
            nodes {
              id
              taxable
              price { amount currencyCode }
              compareAtPrice { amount currencyCode }
              unitPrice { amount currencyCode }
              unitPriceMeasurement { referenceValue referenceUnit }
            }
            pageInfo { hasNextPage }
          }`,
        ],
      };
    }

    set product(value) {
      this._product = value;
      this.render();
    }

    get product() {
      return this._product;
    }

    connectedCallback() {
      this._modeListener ||= () => this.render();
      document.addEventListener('md:pricemode', this._modeListener);
      this.render();
    }

    disconnectedCallback() {
      document.removeEventListener('md:pricemode', this._modeListener);
    }

    render() {
      const settings = window.MundizioCards || {};
      const context = priceContext(this._product, settings);
      this.replaceChildren();
      this.hidden = !context;
      this.removeAttribute('data-price-variant-id');
      if (!context) return;

      const { variant, from } = context;
      const current = money(variant.price);
      const compare = money(variant.compareAtPrice);
      const rate = variant.taxable === false ? 0
        : nonNegative(this._product?.graphql?.rccPriceTaxRate?.value)
          ?? nonNegative(settings.taxRate) ?? 19;
      const hasNet = settings.showNetPrice !== false && rate > 0;
      const netMode = hasNet && window.mdPrice?.enabled !== false && window.mdPrice?.mode === 'net';
      const toNet = (cents) => Math.round(cents / (1 + rate / 100));
      const format = (cents) => new Intl.NumberFormat(settings.locale || 'de-DE', {
        style: 'currency', currency: current.currency,
      }).format(cents / 100);
      const displayed = (cents) => format(netMode ? toNet(cents) : cents);
      const sale = compare && compare.currency === current.currency && compare.cents > current.cents
        && displayed(compare.cents) !== displayed(current.cents);
      const fromText = from ? (settings.fromLabel || 'Ab') : '';
      const netLabel = settings.netLabel ?? 'zzgl. MwSt.';
      const netSuffix = settings.netSuffix ?? netLabel;
      const grossLabel = settings.grossLabel || 'inkl. MwSt.';
      const priceLabel = sale ? (settings.salePriceLabel || 'Angebotspreis')
        : (settings.regularPriceLabel || 'Preis');
      const prefix = (node, extra = '') => {
        if (from) node.append(element('span', `price__from ${extra}`.trim(), fromText), document.createTextNode(' '));
      };

      // Reuse native price styles, but omit `.price` and data-md-net: those are
      // mutation targets of the production Google PDP repricing script.
      const wrapper = element('div', `rcc-search__product__price price--card${hasNet ? ' price--has-net' : ''}${sale ? ' price--on-sale' : ''}`);
      const row = element('div', 'price__default');
      const gross = element('span', 'price__current');
      prefix(gross);
      gross.append(element('span', 'js-value', format(current.cents)),
        document.createTextNode(' '), element('span', 'price__gross-suffix', grossLabel));
      row.append(gross);
      if (sale) {
        const was = element('span', 'price__was');
        was.append(element('span', 'js-value', format(compare.cents)));
        // Native card CSS switches both prices to the same tax basis together.
        if (hasNet) was.append(element('span', 'price__net-was', format(toNet(compare.cents))));
        row.append(was);
      }
      if (hasNet) {
        const net = element('span', 'price__net');
        prefix(net, 'price__net-from');
        net.append(element('span', 'price__net-value', format(toNet(current.cents))));
        // Leerer Zusatz heisst: gar kein Knoten. Ein leerer <span> wuerde das Leerzeichen
        // davor behalten und die Kachel um eine Luecke laenger machen.
        if (netSuffix) {
          net.append(document.createTextNode(' '), element('span', 'price__net-suffix', netSuffix));
        }
        row.append(net);
      }
      wrapper.append(row);

      const accessible = [`${priceLabel}: ${fromText} ${format(netMode ? toNet(current.cents) : current.cents)} ${netMode ? netLabel : grossLabel}`];
      if (sale) accessible.push(`${settings.regularPriceLabel || 'Regulärer Preis'}: ${format(netMode ? toNet(compare.cents) : compare.cents)}`);
      const unit = money(variant.unitPrice);
      const measurement = variant.unitPriceMeasurement;
      const reference = nonNegative(measurement?.referenceValue);
      const units = { CL: 'cl', CM: 'cm', FLOZ: 'fl oz', FT: 'ft', FT2: 'ft²', G: 'g', GAL: 'gal', IN: 'in', ITEM: 'Stück', KG: 'kg', L: 'l', LB: 'lb', M: 'm', M2: 'm²', M3: 'm³', MG: 'mg', ML: 'ml', MM: 'mm', OZ: 'oz', PT: 'pt', QT: 'qt', YD: 'yd' };
      const referenceUnit = units[String(measurement?.referenceUnit || '').toUpperCase()];
      if (unit && unit.currency === current.currency && reference > 0 && referenceUnit) {
        const suffix = `${reference === 1 ? '' : new Intl.NumberFormat(settings.locale || 'de-DE').format(reference)}${referenceUnit}`;
        const separator = settings.unitSeparator || '/';
        const unitRow = element('div', 'unit-price relative');
        unitRow.append(element('span', 'unit-price__price', format(unit.cents)),
          // Native CSS switches units without checking price--has-net. Keep
          // an identical alternate for zero tax / disabled net display.
          element('span', 'unit-price__price unit-price__price--net', format(hasNet ? toNet(unit.cents) : unit.cents)),
          element('span', 'unit-price__separator', ` ${separator} `),
          element('span', 'unit-price__unit', suffix));
        wrapper.append(unitRow);
        accessible.push(`${settings.unitPriceLabel || 'Grundpreis'}: ${format(netMode ? toNet(unit.cents) : unit.cents)} ${separator} ${suffix}`);
      }
      // One current-mode announcement; CSS controls the visible native values.
      wrapper.setAttribute('role', 'group');
      wrapper.setAttribute('aria-label', accessible.join('. ').replace(/\s+/g, ' ').trim());
      Array.from(wrapper.children).forEach((child) => child.setAttribute('aria-hidden', 'true'));
      if (variant.id) this.setAttribute('data-price-variant-id', numericId(variant.id));
      this.append(wrapper);
    }
  }

  if (!customElements.get('rcc-product-price')) customElements.define('rcc-product-price', RccProductPrice);
})();
